import {
  createContext,
  useContext,
  useState,
  useCallback,
  useMemo,
  useEffect,
  useRef,
  type ReactNode,
} from "react";
import { zIndex } from "@/styles/variants";

interface ModalStackItem {
  id: string;
  previousFocusElement: HTMLElement | null;
}

interface ModalStackContextValue {
  registerModal: (id: string, focusElement: HTMLElement | null) => void;
  unregisterModal: (id: string) => void;
  isTopmost: (id: string) => boolean;
  getZIndex: (id: string) => number;
}

const ModalStackContext = createContext<ModalStackContextValue | null>(null);

interface ScrollLockState {
  scrollY: number;
  htmlOverflow: string;
}

export function ModalStackProvider({ children }: { children: ReactNode }) {
  const [stack, setStack] = useState<ModalStackItem[]>([]);
  const scrollLockStateRef = useRef<ScrollLockState | null>(null);
  // Use ref to access current stack in callbacks without causing re-renders
  const stackRef = useRef<ModalStackItem[]>([]);
  stackRef.current = stack;

  // Lock body scroll when any modal is open
  // Uses position: fixed approach which works on iOS Safari (overflow: hidden alone doesn't)
  useEffect(() => {
    if (stack.length > 0) {
      // Capture original values only when first modal opens
      if (scrollLockStateRef.current === null) {
        scrollLockStateRef.current = {
          scrollY: window.scrollY,
          htmlOverflow: document.documentElement.style.overflow,
        };

        // Simple scroll lock via html overflow
        // scrollbar-gutter: stable in CSS keeps the gutter visible
        document.documentElement.style.overflow = "hidden";
      }
    } else if (scrollLockStateRef.current !== null) {
      // Restore original styles when last modal closes
      const { scrollY, htmlOverflow } = scrollLockStateRef.current;

      document.documentElement.style.overflow = htmlOverflow;

      // Restore scroll position
      window.scrollTo(0, scrollY);

      scrollLockStateRef.current = null;
    }
  }, [stack.length]);

  const registerModal = useCallback((id: string, focusElement: HTMLElement | null) => {
    setStack((prev) => {
      // Prevent duplicate registration
      if (prev.some((item) => item.id === id)) return prev;
      return [...prev, { id, previousFocusElement: focusElement }];
    });
  }, []);

  const unregisterModal = useCallback((id: string) => {
    setStack((prev) => {
      const item = prev.find((item) => item.id === id);
      // Restore focus to previous element AFTER React finishes unmounting
      if (item?.previousFocusElement && typeof item.previousFocusElement.focus === "function") {
        queueMicrotask(() => {
          try {
            item.previousFocusElement?.focus();
          } catch (e) {
            // Element may have been removed from DOM
            console.warn("Failed to restore focus:", e);
          }
        });
      }
      return prev.filter((item) => item.id !== id);
    });
  }, []);

  // Use stackRef to avoid recreating these callbacks when stack changes
  const isTopmost = useCallback((id: string) => {
    const currentStack = stackRef.current;
    if (currentStack.length === 0) return false;
    return currentStack[currentStack.length - 1]?.id === id;
  }, []);

  const getZIndex = useCallback((id: string) => {
    const index = stackRef.current.findIndex((item) => item.id === id);
    if (index === -1) return zIndex.modal;
    return zIndex.modal + index;
  }, []);

  const contextValue = useMemo(
    () => ({ registerModal, unregisterModal, isTopmost, getZIndex }),
    [registerModal, unregisterModal, isTopmost, getZIndex]
  );

  return (
    <ModalStackContext.Provider value={contextValue}>
      {children}
    </ModalStackContext.Provider>
  );
}

export function useModalStack() {
  const context = useContext(ModalStackContext);
  if (context) {
    return context;
  }

  // Fallback no-op implementation for contexts rendered outside ModalStackProvider
  return {
    registerModal: () => {},
    unregisterModal: () => {},
    isTopmost: () => true,
    getZIndex: () => zIndex.modal,
  };
}
